import { z } from 'zod';
import { RetrievalMethod } from '@/feature/profiles/constants';
import { ControllerRenderProps, FieldPath } from 'react-hook-form';

import { Facesheet } from '@/feature/profiles/types';
import {
  ProviderFacilityFormData,
  providerFacilitySchema,
} from '../suggested-providers-facility/validation';
import { RequestActionFormsData } from '@/feature/patients/types';
import type {
  PresignedUrlResponse,
  UploadCompletionResponse,
} from '@/types/file-upload';

export interface RequestActionFormData {
  HIE?: {
    alternativePostalCodes?: string[];
  };
  FACESHEETS?: {
    selectedFacesheets?: Facesheet[];
  };
  MEDICATIONS?: {
    retrieveMedications?: boolean;
  };
  FULL?: {
    requestFrom?: string;
    releasedTo?: string;
    retrieveFrom?: ProviderFacilityFormData[];
  };
  RECORD_UPLOAD?: {
    uploadedFiles?: (PresignedUrlResponse | UploadCompletionResponse)[];
  };
}

export type RequestActionFormFieldProps<T extends keyof RequestActionFormData> =
  {
    field: ControllerRenderProps<RequestActionFormData, T>;
  };

// Type for nested field paths like 'FACESHEETS.selectedFacesheets'
export type NestedRequestActionFormFieldProps<
  T extends FieldPath<RequestActionFormData>,
> = {
  field: ControllerRenderProps<RequestActionFormData, T>;
};

const HIEFieldsSchema = z.object({
  alternativePostalCodes: z
    .array(z.string())
    .refine((codes) => codes.every((code) => /^\d{5}$/.test(code)), {
      message: 'ZIP codes must be 5 digits',
    }),
});

const FacesheetsFieldsSchema = z.object({
  selectedFacesheets: z
    .array(
      z.object({
        facesheetConfigId: z.string(),
        organizationId: z.string(),
        organizationProfileId: z.string().optional(),
        displayName: z.string(),
        isOrgWide: z.boolean().optional(),
        isDefault: z.boolean().optional(),
        isExpedited: z.boolean().optional(),
        templateTypes: z.array(z.string()),
      }),
    )
    .min(1, 'At least one facesheet must be selected'),
});

const MedicationDataFieldsSchema = z.object({
  retrieveMedications: z.boolean(),
});

const RetrievalsFieldsSchema = z.object({
  requestFrom: z
    .string()
    .min(1, 'Enter a requesting Provider/Facility.')
    .max(255, 'Requesting Provider/Facility must be 255 characters or less')
    .trim(),
  releasedTo: z
    .string()
    .min(1, 'Enter a releasing Provider/Facility.')
    .max(255, 'Releasing Provider/Facility must be 255 characters or less')
    .trim(),
  retrieveFrom: z
    .array(providerFacilitySchema)
    .min(1, 'Add at least one Provider/Facility.'),
});

const UploadsFieldsSchema = z.object({
  uploadedFiles: z.array(z.any()).min(1, 'At least one file must be uploaded'),
});

export const createValidationSchema = (
  selectedMethods: RetrievalMethod[],
  options?: { allowUnknown?: boolean },
) => {
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  selectedMethods.forEach((method) => {
    switch (method) {
      case RetrievalMethod.HIE:
        schemaFields[method] = HIEFieldsSchema;
        break;
      case RetrievalMethod.FACESHEETS:
        schemaFields[method] = FacesheetsFieldsSchema;
        break;
      case RetrievalMethod.MEDICATIONS:
        schemaFields[method] = MedicationDataFieldsSchema;
        break;
      case RetrievalMethod.PROVIDER_FACILITY:
        schemaFields[method] = RetrievalsFieldsSchema;
        break;
      case RetrievalMethod.RECORD_UPLOAD:
        schemaFields[method] = UploadsFieldsSchema;
        break;
    }
  });

  const schema = z.object(schemaFields);
  return options?.allowUnknown ? schema.passthrough() : schema.strict();
};

export const getDefaultValues = (
  selectedMethods: RetrievalMethod[],
  persistedData?: RequestActionFormsData,
  organizationName?: string,
) => {
  const defaultValues: Record<string, unknown> = {};

  selectedMethods.forEach((method) => {
    switch (method) {
      case RetrievalMethod.HIE:
        defaultValues[method] = {
          alternativePostalCodes:
            persistedData?.[method]?.alternativePostalCodes || [],
        };
        break;
      case RetrievalMethod.FACESHEETS:
        defaultValues[method] = {
          selectedFacesheets: persistedData?.[method]?.selectedFacesheets || [],
        };
        break;
      case RetrievalMethod.MEDICATIONS:
        defaultValues[method] = {
          retrieveMedications:
            persistedData?.[method]?.retrieveMedications ?? true,
        };
        break;
      case RetrievalMethod.PROVIDER_FACILITY:
        defaultValues[method] = {
          requestFrom: persistedData?.[method]?.requestFrom || '',
          releasedTo:
            persistedData?.[method]?.releasedTo || organizationName || '',
          retrieveFrom: persistedData?.[method]?.retrieveFrom || [],
        };
        break;
      case RetrievalMethod.RECORD_UPLOAD:
        defaultValues[method] = {
          uploadedFiles: persistedData?.[method]?.uploadedFiles || [],
        };
        break;
    }
  });

  return defaultValues;
};

export const validateMethodData = (
  method: RetrievalMethod,
  data: Record<string, unknown>,
): boolean => {
  const methodData = data[method];
  if (!methodData) return false;

  try {
    switch (method) {
      case RetrievalMethod.HIE:
        HIEFieldsSchema.parse(methodData);
        return true;
      case RetrievalMethod.FACESHEETS:
        FacesheetsFieldsSchema.parse(methodData);
        return true;
      case RetrievalMethod.MEDICATIONS:
        MedicationDataFieldsSchema.parse(methodData);
        return true;
      case RetrievalMethod.PROVIDER_FACILITY:
        RetrievalsFieldsSchema.parse(methodData);
        return true;
      case RetrievalMethod.RECORD_UPLOAD:
        UploadsFieldsSchema.parse(methodData);
        return true;
      default:
        return false;
    }
  } catch {
    return false;
  }
};
